import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Loader2, QrCode, CreditCard, Copy, CheckCircle2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, inr, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [paid, setPaid] = useState(false);
  const [config, setConfig] = useState(null);
  const [method, setMethod] = useState("upi_qr");
  const [upiOrder, setUpiOrder] = useState(null); // holds {order_id, upi, total}
  const [form, setForm] = useState({
    full_name: user?.name || "",
    phone: "",
    email: user?.email || "",
    address: "",
    city: "",
    state: "",
    pincode: "",
  });

  const shipping = subtotal >= 999 ? 0 : 79;
  const total = subtotal + shipping;

  useEffect(() => {
    api.get("/payment-config").then(({ data }) => {
      setConfig(data);
      setMethod(data.upi_enabled ? "upi_qr" : "razorpay");
    });
  }, []);

  useEffect(() => {
    if (items.length === 0 && !paid && !upiOrder) navigate("/shop");
  }, [items.length, paid, upiOrder, navigate]);

  if (items.length === 0 && !upiOrder) return null;

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    for (const [k, v] of Object.entries(form)) {
      if (!v.trim()) {
        toast.error(`Please fill in your ${k.replace("_", " ")}`);
        return false;
      }
    }
    if (!/^\d{6}$/.test(form.pincode)) {
      toast.error("Enter a valid 6-digit pincode");
      return false;
    }
    if (!/^\d{10}$/.test(form.phone)) {
      toast.error("Enter a valid 10-digit phone number");
      return false;
    }
    return true;
  };

  const finalizeRazorpay = async (order_id, rzpResponse = {}) => {
    try {
      const { data } = await api.post("/orders/verify", {
        order_id,
        razorpay_payment_id: rzpResponse.razorpay_payment_id,
        razorpay_order_id: rzpResponse.razorpay_order_id,
        razorpay_signature: rzpResponse.razorpay_signature,
      });
      setPaid(true);
      clearCart();
      toast.success("Payment successful!");
      navigate(`/order-success/${data.order.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setProcessing(false);
    }
  };

  const startCheckout = async () => {
    if (!user) {
      toast.error("Please log in to checkout");
      navigate("/login?redirect=/checkout");
      return;
    }
    if (!validate()) return;
    setProcessing(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping: form,
        method,
      });

      if (data.method === "upi_qr") {
        setUpiOrder(data);
        setProcessing(false);
        return;
      }

      // Razorpay / sandbox
      if (data.razorpay_enabled && data.razorpay_order_id) {
        const ok = await loadRazorpayScript();
        if (!ok) {
          toast.error("Failed to load payment gateway");
          setProcessing(false);
          return;
        }
        const rzp = new window.Razorpay({
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          name: "Physio Essential",
          description: "Supplement Order",
          order_id: data.razorpay_order_id,
          prefill: {
            name: data.customer.name,
            email: data.customer.email,
            contact: data.customer.contact,
          },
          theme: { color: "#2F80ED" },
          handler: (resp) => finalizeRazorpay(data.order_id, resp),
          modal: { ondismiss: () => { setProcessing(false); toast("Payment cancelled"); } },
        });
        rzp.open();
      } else {
        toast("Processing in test mode…");
        setTimeout(() => finalizeRazorpay(data.order_id), 1200);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setProcessing(false);
    }
  };

  const confirmUpiPaid = async () => {
    setProcessing(true);
    try {
      const { data } = await api.post("/orders/confirm-upi", { order_id: upiOrder.order_id });
      setPaid(true);
      clearCart();
      toast.success("Payment submitted — we'll verify shortly!");
      navigate(`/order-success/${data.order.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setProcessing(false);
    }
  };

  const fields = [
    { k: "full_name", label: "Full Name", span: 2 },
    { k: "email", label: "Email", span: 1 },
    { k: "phone", label: "Phone (10-digit)", span: 1 },
    { k: "address", label: "Address", span: 2 },
    { k: "city", label: "City", span: 1 },
    { k: "state", label: "State", span: 1 },
    { k: "pincode", label: "Pincode", span: 1 },
  ];

  // ---------- UPI QR VIEW ----------
  if (upiOrder) {
    return (
      <div className="min-h-screen pt-[68px] lg:pt-[116px]" data-testid="checkout-upi-view">
        <div className="mx-auto max-w-lg px-5 py-12">
          <h1 className="text-center font-display text-3xl font-bold tracking-tight">Scan &amp; Pay via UPI</h1>
          <p className="mt-2 text-center text-sm text-black/60">
            Pay <span className="font-mono font-bold">{inr(upiOrder.total)}</span> using any UPI app, then confirm below.
          </p>

          <div className="mx-auto mt-8 w-full max-w-sm border border-black/10 bg-white p-6 text-center">
            <div className="mb-1 font-mono text-xs uppercase tracking-overline text-black/50">Pay to</div>
            <div className="font-display text-lg font-bold">{upiOrder.upi.name}</div>
            <img
              src={upiOrder.upi.qr_image}
              alt="UPI QR Code"
              className="mx-auto my-5 w-64 border border-black/10"
              data-testid="upi-qr-image"
            />
            <div className="flex items-center justify-center gap-2 rounded-full bg-[#F3F4F6] px-4 py-2">
              <span className="font-mono text-sm" data-testid="upi-id">{upiOrder.upi.id}</span>
              <button
                onClick={() => { navigator.clipboard?.writeText(upiOrder.upi.id); toast.success("UPI ID copied"); }}
                className="text-black/50 hover:text-[#2F80ED]"
                data-testid="copy-upi-btn"
                aria-label="Copy UPI ID"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-5 flex items-center justify-center gap-3 opacity-70">
              <span className="font-mono text-[10px] uppercase tracking-overline text-black/40">Paytm · GPay · PhonePe · BHIM</span>
            </div>
          </div>

          <button
            onClick={confirmUpiPaid}
            disabled={processing}
            className="btn-glow mt-8 flex w-full items-center justify-center gap-2 bg-black py-4 font-semibold text-white transition-colors hover:bg-[#2F80ED] disabled:opacity-60"
            data-testid="confirm-upi-btn"
          >
            {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            I have completed the payment
          </button>
          <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-overline text-black/40">
            Order confirmed after payment verification
          </p>
        </div>
      </div>
    );
  }

  // ---------- MAIN CHECKOUT ----------
  const rzpLabel = config?.razorpay_enabled
    ? "Cards, UPI, Netbanking & Wallets"
    : "Cards, UPI & Wallets (Test mode)";

  return (
    <div className="min-h-screen pt-[68px] lg:pt-[116px]" data-testid="checkout-page">
      <div className="mx-auto max-w-[1400px] px-5 py-12 sm:px-8">
        <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">Checkout</h1>

        <div className="mt-10 grid gap-12 lg:grid-cols-[1.5fr_1fr]">
          <div>
            <h2 className="mb-6 font-mono text-xs uppercase tracking-overline text-black/50">Shipping Details</h2>
            <div className="grid grid-cols-2 gap-5">
              {fields.map((f) => (
                <div key={f.k} className={f.span === 2 ? "col-span-2" : "col-span-2 sm:col-span-1"}>
                  <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">
                    {f.label}
                  </Label>
                  <Input
                    value={form[f.k]}
                    onChange={upd(f.k)}
                    className="rounded-none border-black/15 focus-visible:ring-[#2F80ED]"
                    data-testid={`checkout-${f.k}`}
                  />
                </div>
              ))}
            </div>

            {/* Payment method */}
            <h2 className="mb-4 mt-10 font-mono text-xs uppercase tracking-overline text-black/50">Payment Method</h2>
            <div className="space-y-3">
              {config?.upi_enabled && (
                <button
                  type="button"
                  onClick={() => setMethod("upi_qr")}
                  className={`flex w-full items-center gap-4 border p-4 text-left transition-colors ${
                    method === "upi_qr" ? "border-[#2F80ED] bg-[#2F80ED]/5" : "border-black/15 hover:border-black/40"
                  }`}
                  data-testid="method-upi_qr"
                >
                  <QrCode className="h-6 w-6 text-[#2F80ED]" />
                  <div className="flex-1">
                    <div className="font-semibold">UPI QR — Scan &amp; Pay</div>
                    <div className="text-xs text-black/50">Paytm, GPay, PhonePe, BHIM &amp; any UPI app</div>
                  </div>
                  <span className={`h-4 w-4 rounded-full border-2 ${method === "upi_qr" ? "border-[#2F80ED] bg-[#2F80ED]" : "border-black/30"}`} />
                </button>
              )}
              <button
                type="button"
                onClick={() => setMethod("razorpay")}
                className={`flex w-full items-center gap-4 border p-4 text-left transition-colors ${
                  method === "razorpay" ? "border-[#2F80ED] bg-[#2F80ED]/5" : "border-black/15 hover:border-black/40"
                }`}
                data-testid="method-razorpay"
              >
                <CreditCard className="h-6 w-6 text-[#2F80ED]" />
                <div className="flex-1">
                  <div className="font-semibold">Razorpay Gateway</div>
                  <div className="text-xs text-black/50">{rzpLabel}</div>
                </div>
                <span className={`h-4 w-4 rounded-full border-2 ${method === "razorpay" ? "border-[#2F80ED] bg-[#2F80ED]" : "border-black/30"}`} />
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="h-fit border border-black/10 bg-white p-8 lg:sticky lg:top-32">
            <h2 className="font-mono text-xs uppercase tracking-overline text-black/50">Order Summary</h2>
            <div className="mt-6 space-y-3">
              {items.map((it) => (
                <div key={it.product_id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="flex-1 text-black/70">
                    {it.name} <span className="font-mono text-black/40">×{it.quantity}</span>
                  </span>
                  <span className="font-mono">{inr(it.price * it.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 space-y-2 border-t border-black/10 pt-4 text-sm">
              <div className="flex justify-between">
                <span className="text-black/60">Subtotal</span>
                <span className="font-mono">{inr(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-black/60">Shipping</span>
                <span className="font-mono">{shipping === 0 ? "FREE" : inr(shipping)}</span>
              </div>
            </div>
            <div className="mt-4 flex justify-between border-t border-black/10 pt-4">
              <span className="font-display text-lg font-bold">Total</span>
              <span className="font-mono text-lg font-bold" data-testid="checkout-total">{inr(total)}</span>
            </div>

            <button
              onClick={startCheckout}
              disabled={processing}
              className="btn-glow mt-8 flex w-full items-center justify-center gap-2 bg-black py-4 font-semibold text-white transition-colors hover:bg-[#2F80ED] disabled:opacity-60"
              data-testid="pay-btn"
            >
              {processing ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Processing…</>
              ) : method === "upi_qr" ? (
                <><QrCode className="h-4 w-4" /> Pay {inr(total)} via UPI</>
              ) : (
                <><Lock className="h-4 w-4" /> Pay {inr(total)}</>
              )}
            </button>
            <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-overline text-black/40">
              Secured payments · Razorpay · UPI · GPay · Paytm
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
